/**
 * The window every moved `simplified_list` caller asks `adminMerchants` for: active merchants, name
 * order, one page. Django's route defaulted to `active_only=True` for a super user and ordered by
 * name, so this is what it returned. Named here rather than repeated at each call site because the
 * four callers have to agree — a switcher showing a different set from the merge tools is the bug this
 * prevents.
 */
export const ADMIN_MERCHANT_LIST_PARAMS = {
  isActive__exact: 'true',
  sort: 'name',
  limit: 1000,
  page: 1
}

export default {
  // API-gateway routes, reached with `cfAxios` rather than the Django client, and
  // keyed by merchant **pkid** rather than UUID. The generator emits one-line
  // arrows from Django's spec and cannot reproduce these.
  uiPreference: merchantId => `/core/v1/merchant/${merchantId}/ui-preference`,
  // Replaces Django's `retrieveMerchantOrganization` for the store whenever a Firebase token is
  // present. camelCase; read it through `toDjangoMerchantShape` (utils/merchant-record.js).
  merchantRecord: merchantId => `/core/v1/merchant/${merchantId}/record`,
  locationSettings: (merchantId, locationId) =>
    `/core/v1/merchant/${merchantId}/location/${locationId}/settings`,
  aiTextsReport: merchantId =>
    `/core/v1/merchant/${merchantId}/report/ai-texts`,
  merchantDmsIntegration: merchantId =>
    `/core/v1/admin/merchant/${merchantId}/dms-integration`,
  // Admin scope: super tenants only, so a merchant-user token gets a 403 from the gateway. Takes no
  // merchant id — unlike its neighbours, it is the list. Rows are camelCase: `{ id, pkid, name,
  // supportEmail, isActive }`, plus `integrationType` once core-service#219 is on dev.
  adminMerchants: () => `/core/v1/admin/merchants`,
  merchantDmsConfig: merchantId =>
    `/connector/v1/admin/merchant/${merchantId}/merchant-config`,
  merchantLocations: merchantId => `/core/v1/merchant/${merchantId}/locations`,
  // Keyed by location pkid; a UUID here is a 404. camelCase, the list's row shape.
  merchantLocation: (merchantId, locationPkid) =>
    `/core/v1/merchant/${merchantId}/locations/${locationPkid}`,
  // `{ DEPOSIT, ACHR }` recipient strings. Any other key, IQR and DDR included, is a 400.
  locationEmailReports: (merchantId, locationPkid) =>
    `/core/v1/merchant/${merchantId}/locations/${locationPkid}/email-reports`,
  importAccounts: merchantId =>
    `/core/v1/merchant/${merchantId}/import-accounts`,
  optOutReport: merchantId => `/core/v1/merchant/${merchantId}/opt-outs`,
  // a customer group's automation (template, weekdays, send time, active).
  // the merchant pkid, the gateway's tenant root; groupPkid is the customer-group pkid. A UUID in
  // this path is a 404.
  groupAutomations: merchantId =>
    `/core/v1/merchant/${merchantId}/group-automations`,
  groupAutomation: (merchantId, groupPkid) =>
    `/core/v1/merchant/${merchantId}/customer-groups/${groupPkid}/automation`,
  // The merchant's templates with bodies and `chat_visible`; visibility is set by template pkid.
  merchantMessageTemplates: merchantId =>
    `/core/v1/merchant/${merchantId}/message-templates`,
  templateVisibility: (merchantId, templatePkid) =>
    `/core/v1/merchant/${merchantId}/message-templates/${templatePkid}/visibility`,
  movePayment: merchantId =>
    `/core/v1/admin/merchant/${merchantId}/move-payment`,
  accountsContactedSummary: merchantId =>
    `/action/v1/merchant/${merchantId}/account-summary`,
  // a Send the composer refused is reported here; messaging-service posts the Slack alert.
  blockedMessageAttempts: merchantId =>
    `/messaging/v1/merchant/${merchantId}/blocked-message-attempts`,
  // Singular `Summary`, unlike the generated Django builders in endpoints.js, which are plural
  // `Summaries`. The two families serve the same rows; only these three go through the gateway.
  dashboardAutopaySummary: merchantId =>
    `/core/v1/merchant/${merchantId}/dashboard/autopay-summary`,
  dashboardPromiseSummary: merchantId =>
    `/core/v1/merchant/${merchantId}/dashboard/promise-summary`,
  dashboardScheduledSummary: merchantId =>
    `/core/v1/merchant/${merchantId}/dashboard/scheduled-summary`,
  // Named for the resource, not the operation: endpoints.js spreads this object first, so a key
  // matching the generated Django `listBusinessOwners` / `createBusinessOwner` /
  // `retrieveBusinessOwner` / `updateBusinessOwner` would be silently overwritten by it.
  businessOwners: merchantId =>
    `/core/v1/merchant/${merchantId}/business-owners`,
  businessOwner: (merchantId, ownerId) =>
    `/core/v1/merchant/${merchantId}/business-owners/${ownerId}`,

  // Multi-line query-string builder. The generator emits one-line arrows only.
  customerAccountsWithActions: (merchant_org_id, params = {}) => {
    const queryParams = new URLSearchParams()

    if (params.action_types) {
      queryParams.append('action_types', params.action_types)
    }
    if (params.action_user_id !== undefined) {
      queryParams.append('action_user_id', params.action_user_id)
    }
    if (params.user_id !== undefined) {
      queryParams.append('user_id', params.user_id)
    }
    if (params.action_date_from) {
      queryParams.append('action_date_from', params.action_date_from)
    }
    if (params.action_date_to) {
      queryParams.append('action_date_to', params.action_date_to)
    }
    if (params.page) {
      queryParams.append('page', params.page)
    }
    if (params.page_size) {
      queryParams.append('page_size', params.page_size)
    }

    const queryString = queryParams.toString()
    const baseUrl = `/api/merchants/${merchant_org_id}/customer-accounts/`

    return queryString ? `${baseUrl}?${queryString}` : baseUrl
  }
}
